import { getDataset } from "./agent-dataset-cache";
import {
  inferChartXAxis,
  inferChartXKey,
  isTimeColumnName,
  parseEpochMs,
} from "./chart-axis-utils";
import type { AgentChartInput } from "./validate-chart-spec";

export interface AgentChartRow {
  label: string;
  x: string | number;
  /** Epoch ms when xAxis is time (for tick formatting). */
  xMs?: number;
  value: number;
  /** Original signed value (pie uses |value| for slice size). */
  rawValue?: number;
  [seriesKey: string]: string | number | null | undefined;
}

function readNumeric(raw: unknown): number | null {
  if (raw == null) return null;
  const v = Number(raw);
  return Number.isFinite(v) ? v : null;
}

/** Build Recharts-ready rows from a cached dataset. */
export function buildAgentChartRows(
  spec: AgentChartInput,
  seriesKeys: string[],
): AgentChartRow[] {
  const ds = getDataset(spec.datasetId);
  if (!ds) return [];

  const xKey =
    spec.xKey && ds.columns.includes(spec.xKey)
      ? spec.xKey
      : inferChartXKey(ds.columns, spec.labelKey);
  const xAxis = inferChartXAxis(xKey, spec.xAxis);
  const useSeries = spec.chartType === "line" && seriesKeys.length > 0;
  const isPie = spec.chartType === "pie";
  const useTimeX = xAxis === "time";

  const rows: AgentChartRow[] = [];

  for (let index = 0; index < ds.rows.length; index++) {
    const row = ds.rows[index]!;
    const label = String(row[spec.labelKey] ?? "");
    let x: string | number;
    let xMs: number | undefined;

    if (xAxis === "index") {
      x = index + 1;
    } else if (useTimeX || (typeof xKey === "string" && isTimeColumnName(xKey))) {
      const ms = parseEpochMs(row[xKey]);
      if (ms != null) {
        xMs = ms;
        x = ms;
      } else {
        x = String(row[xKey] ?? label);
      }
    } else {
      x = String(row[xKey] ?? label);
    }

    const point: AgentChartRow = { label, x, value: 0, ...(xMs != null ? { xMs } : {}) };

    if (useSeries) {
      let hasAny = false;
      for (const key of seriesKeys) {
        const v = readNumeric(row[key]);
        if (v != null) hasAny = true;
        point[key] = v;
      }
      if (hasAny) rows.push(point);
      continue;
    }

    const raw = readNumeric(row[spec.valueKey]);
    const signed = raw ?? 0;
    if (isPie) {
      const magnitude = Math.abs(signed);
      if (magnitude === 0) continue;
      point.value = magnitude;
      point.rawValue = signed;
    } else {
      point.value = signed;
    }

    rows.push(point);
  }

  if (useTimeX && rows.length > 1 && typeof rows[0]?.x === "number") {
    rows.sort((a, b) => Number(a.x) - Number(b.x));
  }

  return rows;
}
